import { each, get, isNull, isEmpty, isNumber, isObject, isUndefined, set } from 'lodash';
import { t } from '@lingui/macro';
import { useCallback, useState } from 'react';
import { dateTimeFormatter } from '../../../utilities/FormattingUtilities';
import {
    AGENT_2K_USING_3K_FIELD,
    AGENT_3K_USING_3K_FIELD,
    COUNTS_FORMATTED_FIELD,
    EXPIRED_TEXT,
    FORMATTED_EXPIRATION_DATE_FIELD,
    IS_OVER_LIMIT_FIELD,
    LICENSE_DISABLED_COUNT_FORMAT_FIELD,
    LICENSE_SERVER_OBJECT,
    LICENSE_TYPE_EVALUATION,
    LICENSE_TYPE_EXPIRED,
    LICENSE_TYPE_NONE,
    LICENSE_TYPE_PERMANENT,
    LICENSED_FIELD,
    SHOW_FAKE_COLLECTOR_FIELD,
    SHOW_MONITORED_ELEMENTS_FIELD,
} from './LicenseConstants';
import { useDataService } from '../../../services/ApiDataService';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';
import { useRequestState } from '../../../utilities/Hooks';

const readApiUrl = '/query/license';
const collectorsApiUrl = '/ipm/v1/admin/collectors/distributed';

/**
 * @returns {{
 *  getLicense: () => Promise<void>,
 *  isLoading: boolean,
 *  license: any
 * }}
 */
export function useLicense() {
    const dataService = useDataService();
    const { isLoading, methods } = useRequestState();
    const [license, setLicense] = useState({});
    const [isIpmDisabled] = useIsIpmDisabled();

    const getLicense = useCallback(async () => {
        methods.onInit();
        try {
            const lic = await dataService.get(readApiUrl);
            const collector = await dataService.get(collectorsApiUrl);
            const formatted = formatLicenseInfo(lic, get(collector, 'total', 0) > 0, isIpmDisabled);
            setLicense(formatted);
            methods.onLoad(formatted);
        } catch (e) {
            methods.onError(e);
        }
    }, [dataService, methods, isIpmDisabled]);

    return {
        getLicense,
        isLoading,
        license,
    };
}

function formatLicenseInfo(licenses, isDistributed, isIpmDisabled) {
    each(licenses, formatLicense);
    formatAgent3kBreakout(licenses.agents3k);

    // Show a fake collector if they have a license (but are not distributed)
    set(
        licenses,
        `${LICENSE_SERVER_OBJECT}.${SHOW_FAKE_COLLECTOR_FIELD}`,
        get(licenses, `${LICENSE_SERVER_OBJECT}.${LICENSED_FIELD}`) && !isDistributed && !isIpmDisabled
    );

    // Show a fake collector if they have a license (but are not distributed)
    set(
        licenses,
        `${LICENSE_SERVER_OBJECT}.${SHOW_MONITORED_ELEMENTS_FIELD}`,
        get(licenses, `${LICENSE_SERVER_OBJECT}.${LICENSED_FIELD}`) && !isIpmDisabled
    );

    return licenses;
}

function formatLicense(licenseItem, licenseClasses) {
    if (isNull(licenseItem) || !isObject(licenseItem)) {
        return;
    }

    formatExpiration(licenseItem);
    formatLicenseType(licenseItem);
    formatLicenseCounts(licenseItem);
    formatUnlicensed(licenseItem);
    formatLimits(licenseItem, licenseClasses);
}

export function formatExpiration(licenseItem) {
    const unformattedExpiration = licenseItem?.expirationDate || licenseItem?.subExpirationDate;
    if (!isEmpty(unformattedExpiration)) {
        let formatted = dateTimeFormatter(unformattedExpiration);

        if (licenseItem.licensed) {
            const daysUntilText =
                licenseItem?.daysUntilExpiration > 1 ? t`Days Until Expiration` : t`Day Until Expiration`;

            formatted += ` (${licenseItem.daysUntilExpiration} ${daysUntilText})`;
        } else {
            formatted += ` (${EXPIRED_TEXT})`;
        }

        licenseItem[FORMATTED_EXPIRATION_DATE_FIELD] = formatted;
    }
}

function formatLicenseType(licenseItem) {
    licenseItem.licenseType = getLicenseType(licenseItem);
}

function formatLicenseCounts(licenseItem) {
    const { currentCount } = licenseItem;
    const licenseCount = licenseItem.count;

    if (!isNull(currentCount) && !isUndefined(currentCount) && !isNull(licenseCount) && !isUndefined(licenseCount)) {
        licenseItem[COUNTS_FORMATTED_FIELD] = `${currentCount} / ${licenseCount}`;
    } else {
        licenseItem[COUNTS_FORMATTED_FIELD] = '0 / 0';
    }
}

function getLicenseType(licenseItem) {
    if (licenseItem.licensed) {
        if (licenseItem.evaluation) {
            return LICENSE_TYPE_EVALUATION;
        }

        return LICENSE_TYPE_PERMANENT;
    }

    if (!licenseItem.expirationDate) {
        return LICENSE_TYPE_NONE;
    }

    return LICENSE_TYPE_EXPIRED;
}

function formatUnlicensed(licenseItem) {
    const { licenseDisabledCount } = licenseItem;
    if (!licenseDisabledCount) {
        return;
    }

    const disabledText = licenseDisabledCount > 1 ? t`has been disabled` : t`have been disabled`;

    licenseItem[LICENSE_DISABLED_COUNT_FORMAT_FIELD] = `${licenseDisabledCount} ${disabledText}`;
}

function formatLimits(licenseItem) {
    const { currentCount, count } = licenseItem;

    if (isNumber(currentCount) && isNumber(count)) {
        licenseItem[IS_OVER_LIMIT_FIELD] = currentCount > count;
    }
}

function formatAgent3kBreakout(licenseItem) {
    // Handle the logic around 2ks using 3k licenses
    // We'll break down counts if 2ks are using 3k licenses
    const num2kUsing3kLic = get(licenseItem, 'numberOf2ksUsing3kLicense', 0);
    if (num2kUsing3kLic > 0) {
        let actual3kCount = get(licenseItem, 'currentCount', 0) - num2kUsing3kLic;
        if (actual3kCount < 1) {
            actual3kCount = 0;
        }

        const agent2kText = num2kUsing3kLic > 1 ? t`nPoint 2000s` : t`nPoint 2000`;
        const agent3kText = actual3kCount > 1 ? t`nPoint 3000s` : t`nPoint 3000`;

        licenseItem[AGENT_2K_USING_3K_FIELD] = `(${num2kUsing3kLic} ${agent2kText})`;
        licenseItem[AGENT_3K_USING_3K_FIELD] = `(${actual3kCount} ${agent3kText})`;
    }
}
